// These types are used BY onprem instances, i.e. the On-Prem Admin page (wrapping skydioctl responses)
// not remote On-Prem orgs
export interface OnpremResponse {
  error: string | null;
  message: string;
}

export interface OnpremResponseVersion extends OnpremResponse {
  version: string;
}

export interface OnpremResponseServerConfigUpdate extends OnpremResponse {
  isDowngrade: boolean;
  isOutOfSync: boolean;
}

// This should match the skydioctl definition of PartitionInfo in pkg/server/support.go
interface PartitionInfo {
  device: string;
  mountpoint: string;
  sizeHuman: string;
  usedHuman: string;
  usedPercent: number;
}

export interface OnpremResponseDisk extends OnpremResponse {
  partitionInfo: PartitionInfo[];
}

export interface OnpremResponseDiscover extends OnpremResponse {
  // discovered is a map of requested items to their values
  // NOTE: The keys are forced into camelCase, so if you request
  // the env var "ADVERTISE_IP", the key will be "advertiseIp"
  // (this is just because this is how everything in the Cloud API helpers are expected to work)
  discovered: Record<string, string>;
}

export interface OnpremResponseSCCurrentTimestamp extends OnpremResponse {
  generatedTimestamp: number;
}

export interface OnpremRequestRtspSettings {
  rtsp_port: number | null;
  rtsps_port: number | null;
  static_token_id: string | null;
  static_token_secret: string | null;
}

export interface OnpremResponseRtspSettings extends OnpremResponse {
  rtspSettings: {
    rtspPort: number | null;
    rtspsPort: number | null;
    staticTokenId: string | null;
    staticTokenSecret: string | null;
  };
}

// Mirrors the RemoteIdMode enum in cloud/onprem/skydioctl/pkg/server/remote_id.go.
// Keep in sync.
export const REMOTE_ID_MODE = {
  DISABLED: "disabled",
  ENABLED: "enabled",
  FIXED_LOCATION: "fixed-location",
} as const;

export type RemoteIdMode = (typeof REMOTE_ID_MODE)[keyof typeof REMOTE_ID_MODE];

export interface OnpremRequestRemoteIdSettings {
  mode: RemoteIdMode;
}

export interface OnpremRequestDocklessSitesSettings {
  enabled: boolean;
}

export interface OnpremRequestPathfinderSettings {
  enabled: boolean;
}

export interface OnpremResponseStageSelfSigned extends OnpremResponse {
  caCert: string;
}

export interface OnpremResponseBundleCreate extends OnpremResponse {
  jobId: string;
}

// Bundle job status strings - must match the constants in cloud/onprem/skydioctl/pkg/server/support_bundle.go
export const BUNDLE_JOB_STATUS = {
  PENDING: "pending",
  RUNNING: "running",
  COMPLETE: "complete",
  ERROR: "error",
} as const;

export type BundleJobStatus = (typeof BUNDLE_JOB_STATUS)[keyof typeof BUNDLE_JOB_STATUS];

export interface OnpremResponseBundleStatus extends OnpremResponse {
  status: BundleJobStatus;
  bundleUrl?: string;
}

export interface OnpremResponseFeatureFlags extends OnpremResponse {
  feature_flags: Record<string, boolean | null>;
}

// Keep in sync with ONPREM_PATHFINDER_STATUS_* in
// cloud/temporal/workflows/onprem_pathfinder_ingest/constants.py
export const PATHFINDER_TILESET_STATUS = {
  INSTALLED: "installed",
  IN_PROGRESS: "in_progress",
  NOT_INSTALLED: "not_installed",
} as const;

export type PathfinderTilesetStatus =
  (typeof PATHFINDER_TILESET_STATUS)[keyof typeof PATHFINDER_TILESET_STATUS];

export interface OnpremResponsePathfinderTilesetStatus extends OnpremResponse {
  status: PathfinderTilesetStatus;
  tileCount?: number;
}

export interface OnpremResponsePathfinderUploadCredentials extends OnpremResponse {
  credentials: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken: string;
  };
  region: string;
  bucket: string;
  key: string;
  endpointUrl?: string;
}

export function onpremResponseErrorString(response: OnpremResponse): string {
  // Should only be called if there IS an error, this is to make linting happy
  response.error = response.error || "unknown error";
  return response.message ? `${response.message}: ${response.error}` : response.error;
}
